import type { ReactElement } from 'react'

import { Icon } from '@maria-cavalheiro-website/shared-ui-web'

import type { PoemCardView } from './poem-card-view.ts'

export interface PoemCardProps {
  readonly view: PoemCardView
}

const READ_LABEL = 'Ler poema'

/**
 * Leaf presenter for one poem in the index.
 *
 * The whole card is NOT one `<a>`. A link wrapping a heading, a badge and a
 * decorative icon produces an accessible name like "Pássaros A janela Ler poema",
 * which is what a screen-reader user would hear for all 23 cards in a row. Instead
 * the link sits inside the `<h4>` and its accessible name is the poem title alone;
 * a `::after` overlay in CSS extends the clickable area to the whole card, so the
 * pointer affordance is unchanged.
 *
 * Heading level is fixed at 4 because the index is always h2 (section) -> h3
 * (group) -> h4 (poem). Making it a prop would invite a caller to break the
 * contiguity the whole outline depends on.
 */
export function PoemCard({ view }: PoemCardProps): ReactElement {
  return (
    <article className="poem-card">
      <div className="poem-card__top">
        <span className={`poem-card__badge-icon poem-card__badge-icon--${view.tone}`}>
          <Icon name={view.icon} size={21} decorative />
        </span>
        <span className="poem-card__badge">{view.badge}</span>
      </div>

      <div className="poem-card__body">
        <h4 className="poem-card__title">
          <a className="poem-card__link" href={view.href}>
            {view.title}
          </a>
        </h4>
        <span className="poem-card__cta">{READ_LABEL}</span>
      </div>
    </article>
  )
}
